import type { SessionUser, User } from "./user"

/** Matches backend ``BillingMode`` (camelCase in JSON). */
export type BillingMode = "standard" | "managed_by_service_bureau"
export type ManagedBillingMode = "bureau_pool" | "self_billed" | "bureau_backed"

/** Inactive billing reasons surfaced on auth payloads (camelCase in JSON). */
export type BillingInactiveReason =
  | "billing_owner_inactive"
  | "billing_owner_requires_payment"
  | "managed_business_deactivated"
  | "no_billing_context"
  | "service_bureau_setup_pending"
  | "service_bureau_payment_processing"
  | "contract_subscription_mismatch"

/** Resolved billing identity for the operating business (from /auth/token/check). */
export interface BillingContext {
  mode: BillingMode
  managedBillingMode: ManagedBillingMode | null
  isBillingActive: boolean
  requiresPaymentUpdate: boolean
  isTrialActive: boolean
  inactiveReason: BillingInactiveReason | null
  serviceBureau: { id: number; name: string } | null
  burnStripeManagementId?: string | null
  collectionStripeManagementId?: string | null
  tenantCollectible?: boolean | null
  tenantCollectionReason?: string | null
}

/**
 * True when billing context indicates an active contract with an inactive subscription.
 */
export function isContractSubscriptionMismatch(
  billingContext: BillingContext | null | undefined
): boolean {
  return billingContext?.inactiveReason === "contract_subscription_mismatch"
}

/**
 * True when the operating business is bureau-pooled or bureau-backed and
 * should not see self-serve billing settings.
 */
export function shouldHideBillingSettings(
  billingContext: BillingContext | null | undefined
): boolean {
  return (
    billingContext?.mode === "managed_by_service_bureau" &&
    (billingContext.managedBillingMode === "bureau_pool" ||
      billingContext.managedBillingMode === "bureau_backed")
  )
}

/**
 * Which client a login request originated from. Recorded for attribution and,
 * for Google auth, to select the OAuth client ID. Only the intake app sends a
 * source today; other clients omit it.
 */
export type LoginSource = "intake"

/**
 * A request to log in a user.
 */
export interface LoginRequest {
  email: string
  password: string
  businessSlug?: string
  source?: LoginSource
}

/**
 * The response from a login attempt (POST api/auth/token/).
 */
export interface LoginResponse {
  user: User
  billingContext?: BillingContext | null
  requiresAction?: Record<string, boolean>
  message?: Record<string, string>
}

/**
 * A business the user can choose when their email is associated with multiple businesses.
 */
export interface BusinessOption {
  businessId: string
  businessName: string
  slug: string
  allowed: boolean
}

/**
 * Response returned when the backend requires the user to pick a business.
 */
export interface BusinessSelectionContent {
  status: "business_selection_required"
  businesses: BusinessOption[]
}

/**
 * Response returned when the account's email is not yet verified. The user must
 * enter the emailed code before a session (web) or tokens (desktop) are issued.
 * A discriminated "next step" variant, mirroring BusinessSelectionContent.
 */
export interface EmailVerificationContent {
  status: "email_verification_required"
  email: string
}

/**
 * Union of content shapes returned by the login endpoint.
 * RequestBuilder extracts `response.data.content`, so we get one of these shapes.
 */
export type LoginApiContent =
  | LoginResponse
  | BusinessSelectionContent
  | EmailVerificationContent

/**
 * Type guard: checks whether the login content is a business-selection prompt.
 */
export function isBusinessSelection(
  content: LoginApiContent
): content is BusinessSelectionContent {
  return "status" in content && content.status === "business_selection_required"
}

/**
 * Type guard: checks whether the login content is an email-verification prompt.
 */
export function isEmailVerificationRequired(
  content: LoginApiContent
): content is EmailVerificationContent {
  return (
    "status" in content && content.status === "email_verification_required"
  )
}

/**
 * The response from a session check (GET api/auth/token/check/).
 */
export interface AuthSessionResponse {
  authenticated: boolean
  user?: SessionUser
  billingContext?: BillingContext | null
}
